import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import apiService from '../../services/apiService';



const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Page = styled.div`
  min-height: 100vh; background: #0a0a0f; color: #e2e8f0;
  font-family: 'Inter', 'Pretendard', sans-serif; padding: 20px;
`;
const TopBar = styled.div`
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 28px; padding-bottom: 20px; border-bottom: 1px solid #1e2030;
`;
const BackBtn = styled.button`
  background: none; border: none; color: #64748b; cursor: pointer;
  padding: 8px; border-radius: 8px; transition: all 0.2s;
  &:hover { background: #1e2030; color: #e2e8f0; }
`;
const PageTitle = styled.h1`
  font-size: 20px; font-weight: 800; margin: 0;
  background: linear-gradient(135deg, #10b981, #06b6d4);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;

const NavTabs = styled.div`display: flex; gap: 8px; margin-left: auto;`;
const NavTab = styled.button`
  background: ${p => p.active ? 'rgba(16,185,129,0.15)' : 'transparent'};
  border: 1px solid ${p => p.active ? '#10b981' : '#1e2030'};
  color: ${p => p.active ? '#10b981' : '#64748b'};
  padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
  &:hover { border-color: #10b981; color: #10b981; }
`;

const RefreshBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  background: rgba(16,185,129,0.1); border: 1px solid #10b981;
  color: #10b981; padding: 8px 14px; border-radius: 8px;
  font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s;
  &:hover { background: rgba(16,185,129,0.2); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const SpinIcon = styled(RefreshCw)`
  animation: ${p => p.$spinning ? css`${spin} 1s linear infinite` : 'none'};
`;

const Table = styled.div`
  background: #111827; border: 1px solid #1e2030; border-radius: 16px;
  overflow: hidden; animation: ${fadeIn} 0.4s ease; margin-bottom: 24px;
`;
const TableHead = styled.div`
  display: grid; grid-template-columns: ${p => p.cols};
  background: #0f1724; padding: 12px 16px;
  border-bottom: 1px solid #1e2030; gap: 8px;
`;
const TH = styled.div`font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em;`;
const TableRow = styled.div`
  display: grid; grid-template-columns: ${p => p.cols};
  padding: 14px 16px; gap: 8px;
  border-bottom: 1px solid #0f1724;
  cursor: ${p => p.clickable ? 'pointer' : 'default'};
  transition: background 0.15s;
  &:hover { background: ${p => p.clickable ? '#1a2235' : 'transparent'}; }
  &:last-child { border-bottom: none; }
`;
const TD = styled.div`font-size: 13px; color: #e2e8f0; display: flex; align-items: center; gap: 6px;`;

const Badge = styled.span`
  font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 6px; white-space: nowrap;
  background: ${p =>
    p.type === 'SYS2' ? 'rgba(6,182,212,0.15)' :
    p.type === 'SYS1' ? 'rgba(16,185,129,0.15)' :
    p.type === 'OPEN' ? 'rgba(16,185,129,0.15)' :
    p.type === 'CLOSED' ? 'rgba(100,116,139,0.15)' :
    'rgba(100,116,139,0.15)'};
  color: ${p =>
    p.type === 'SYS2' ? '#06b6d4' :
    p.type === 'SYS1' ? '#10b981' :
    p.type === 'OPEN' ? '#10b981' :
    p.type === 'CLOSED' ? '#64748b' :
    '#64748b'};
`;

const PnlText = styled.span`
  font-weight: 700; color: ${p => p.pos ? '#10b981' : p.pos === false ? '#ef4444' : '#64748b'};
`;

const UnitDetail = styled.div`
  grid-column: 1 / -1;
  background: #0d1520; border-top: 1px solid #1e2030;
  padding: 16px; animation: ${fadeIn} 0.2s ease;
`;
const UnitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
`;
const UnitCard = styled.div`
  background: #111827; border: 1px solid #1e2030; border-radius: 10px;
  padding: 12px; font-size: 12px;
`;
const UnitLabel = styled.div`color: #64748b; margin-bottom: 6px; font-weight: 600;`;
const UnitVal = styled.div`color: #e2e8f0; font-weight: 700;`;

const EmptyState = styled.div`
  text-align: center; padding: 60px 20px; color: #475569;
`;

const COLS_OPEN = '1.5fr 1fr 1fr 1fr 1fr 1fr 1fr 40px';
const COLS_CLOSED = '1.5fr 1fr 1fr 1fr 1fr 1fr';

function TurtlePositionsPage() {
  const navigate = useNavigate();
  const [openPos, setOpenPos] = useState([]);
  const [allPos, setAllPos] = useState([]);
  const [tab, setTab] = useState('open');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [openRes, allRes] = await Promise.all([
        apiService.getTurtleOpenPositions(),
        apiService.getTurtleAllPositions(),
      ]);
      setOpenPos(Array.isArray(openRes) ? openRes : []);
      setAllPos(Array.isArray(allRes) ? allRes : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmt = n => n != null ? Number(n).toLocaleString() : '-';
  const fmtPct = n => n != null ? `${n > 0 ? '+' : ''}${Number(n).toFixed(2)}%` : '-';
  const fmtDate = s => s ? String(s).slice(0, 10) : '-';

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs/turtle')}><ArrowLeft size={18} /></BackBtn>
        <PageTitle>🐢 포지션 현황</PageTitle>
        <NavTabs>
          <NavTab onClick={() => navigate('/labs/turtle')}>대시보드</NavTab>
          <NavTab active onClick={() => navigate('/labs/turtle/positions')}>포지션</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/journal')}>거래일지</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/multi-sim')}>멀티 시뮬</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/screener')}>스크리너</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/settings')}><Settings size={13} /></NavTab>
        </NavTabs>
        <RefreshBtn onClick={load} disabled={loading}>
          <SpinIcon size={13} $spinning={loading} />
          새로고침
        </RefreshBtn>
      </TopBar>

      <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
        <NavTab active={tab === 'open'} onClick={() => setTab('open')}>
          오픈 포지션 ({openPos.length})
        </NavTab>
        <NavTab active={tab === 'all'} onClick={() => setTab('all')}>
          전체 이력 ({allPos.length})
        </NavTab>
      </div>

      {loading ? (
        <EmptyState>로딩 중...</EmptyState>
      ) : tab === 'open' ? (
        openPos.length === 0 ? (
          <EmptyState>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>현재 오픈 포지션이 없습니다</div>
            <div style={{ fontSize: 13, marginTop: 8 }}>신호 스캔 후 진입 신호 발생 시 자동 기록됩니다</div>
          </EmptyState>
        ) : (
          <Table>
            <TableHead cols={COLS_OPEN}>
              <TH>종목</TH><TH>시스템</TH><TH>Unit</TH>
              <TH>평균진입가</TH><TH>현재가</TH><TH>미실현 손익</TH><TH>스탑로스</TH><TH></TH>
            </TableHead>
            {openPos.map(pos => {
              const minStop = pos.units ? Math.min(...pos.units.map(u => u.stopLoss)) : 0;
              const isExpanded = expandedId === pos.symbol;
              return (
                <React.Fragment key={pos.symbol}>
                  <TableRow cols={COLS_OPEN} clickable onClick={() => setExpandedId(isExpanded ? null : pos.symbol)}>
                    <TD>
                      <div>
                        <div style={{ fontWeight: 700 }}>{pos.name}</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{pos.symbol}</div>
                      </div>
                    </TD>
                    <TD><Badge type={pos.system}>{pos.system}</Badge></TD>
                    <TD style={{ fontWeight: 700 }}>{pos.totalUnits}/4</TD>
                    <TD>{fmt(pos.avgEntryPrice)}원</TD>
                    <TD style={{ fontWeight: 700 }}>{fmt(pos.currentPrice)}원</TD>
                    <TD>
                      <PnlText pos={pos.unrealizedRate > 0 ? true : pos.unrealizedRate < 0 ? false : undefined}>
                        {fmtPct(pos.unrealizedRate)}
                      </PnlText>
                      <span style={{ fontSize: 11, color: '#475569' }}>
                        ({fmt(pos.unrealizedPnl)}원)
                      </span>
                    </TD>
                    <TD style={{ color: '#ef4444' }}>{fmt(minStop)}원</TD>
                    <TD>{isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</TD>
                  </TableRow>
                  {isExpanded && pos.units && (
                    <UnitDetail>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#10b981', marginBottom: 12 }}>
                        Unit 상세 | N={fmt(pos.currentN)} | 진입일: {fmtDate(pos.openedAt)}
                      </div>
                      <UnitGrid>
                        {pos.units.map(u => (
                          <UnitCard key={u.unitNo}>
                            <UnitLabel>Unit {u.unitNo}</UnitLabel>
                            <UnitVal>진입가: {fmt(u.entryPrice)}원</UnitVal>
                            <UnitVal style={{ marginTop: 4 }}>수량: {fmt(u.quantity)}주</UnitVal>
                            <UnitVal style={{ marginTop: 4, color: '#ef4444' }}>스탑: {fmt(u.stopLoss)}원</UnitVal>
                            <UnitVal style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>
                              진입일: {fmtDate(u.entryDate)}
                            </UnitVal>
                            <UnitVal style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>
                              비용: {fmt(u.cost)}원
                            </UnitVal>
                          </UnitCard>
                        ))}
                      </UnitGrid>
                    </UnitDetail>
                  )}
                </React.Fragment>
              );
            })}
          </Table>
        )
      ) : (
        allPos.length === 0 ? (
          <EmptyState>포지션 이력이 없습니다</EmptyState>
        ) : (
          <Table>
            <TableHead cols={COLS_CLOSED}>
              <TH>종목</TH><TH>시스템</TH><TH>진입일</TH><TH>청산일</TH><TH>손익률</TH><TH>상태</TH>
            </TableHead>
            {allPos.map((pos, i) => (
              <TableRow key={i} cols={COLS_CLOSED}>
                <TD>
                  <div>
                    <div style={{ fontWeight: 700 }}>{pos.name}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{pos.symbol}</div>
                  </div>
                </TD>
                <TD><Badge type={pos.system}>{pos.system}</Badge></TD>
                <TD style={{ color: '#94a3b8' }}>{fmtDate(pos.openedAt)}</TD>
                <TD style={{ color: '#94a3b8' }}>{pos.exitDate || '-'}</TD>
                <TD>
                  {pos.pnlRate != null ? (
                    <PnlText pos={pos.pnlRate > 0 ? true : pos.pnlRate < 0 ? false : undefined}>
                      {fmtPct(pos.pnlRate)}
                    </PnlText>
                  ) : '-'}
                </TD>
                <TD><Badge type={pos.status}>{pos.status}</Badge></TD>
              </TableRow>
            ))}
          </Table>
        )
      )}
    </Page>
  );
}

export default TurtlePositionsPage;
